import { addDays, endOfDay, endOfToday, getTime, startOfDay } from 'date-fns';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Range, RangeKeyDict } from 'react-date-range';
import { MdOutlineFileDownload } from 'react-icons/md';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { reportsPerformanceService } from 'services';
import { useAuth } from 'hooks';
import { QueryParam } from 'types';
import {
  PerformanceTable,
  SelectedPeriodResume,
} from 'pages/performance/components';
import {
  Button,
  DateRangePicker,
  Header,
  Heading,
  Loading,
  Main,
  TableFooter,
  Text,
} from 'components';
import { FontWeight } from 'commons';
import * as S from './styles';

const DEFAULT_PAST_SELECTION = -30;

export function PerformancePage() {
  const { selectedStore } = useAuth();
  const [searchParams] = useSearchParams();

  const queryStartDate = searchParams.get(QueryParam.startDate);
  const queryEndDate = searchParams.get(QueryParam.endDate);

  const initialRangeValues: Range = {
    startDate: startOfDay(addDays(new Date(), DEFAULT_PAST_SELECTION)),
    endDate: endOfToday(),
  };

  if (queryStartDate && queryEndDate) {
    const qStartDateTimestamp = Number(queryStartDate);
    const qEndDateTimestamp = Number(queryEndDate);

    initialRangeValues.startDate = startOfDay(qStartDateTimestamp);
    initialRangeValues.endDate = endOfDay(qEndDateTimestamp);
  }

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [exportLoading, setExportLoading] = useState(false);

  const store = selectedStore.name || '';

  const { values, setFieldValue, submitForm, resetForm } = useFormik({
    initialValues: initialRangeValues,

    onSubmit: () => {
      mutateTable();
      mutateResume();
    },

    onReset: () => {
      submitForm();
    },
  });

  useEffect(() => {
    setCurrentPage(0);
  }, [store]);

  function changeDateRange(dateRange: RangeKeyDict) {
    const { startDate, endDate } = dateRange.range1;

    if (!startDate || !endDate) return;

    const start = startOfDay(startDate);
    const end = endOfDay(endDate);

    setFieldValue('startDate', start);
    setFieldValue('endDate', end);
  }

  const params = {
    endDate: getTime(values.endDate!),
    startDate: getTime(values.startDate!),
    store: store,
    pageSize: pageSize.toString(),
    currentPage: currentPage.toString(),
  };

  const {
    data: performanceResume,
    isLoading: isResumeLoading,
    mutate: mutateResume,
  } = useSWR(
    `performance-resume-${store}`,
    () => {
      const { endDate, startDate, store } = params;
      const response = reportsPerformanceService.getResume({
        endDate,
        startDate,
        store,
      });
      return response;
    },
    { revalidateOnFocus: false }
  );

  const {
    data: performanceData,
    isLoading: isTableLoading,
    mutate: mutateTable,
    isValidating,
  } = useSWR(
    `performance-${store}`,
    () => {
      const response = reportsPerformanceService.getReport(params);
      return response;
    },
    { revalidateOnFocus: false }
  );

  function changePage(page: number) {
    setCurrentPage(page);

    setTimeout(() => {
      mutateTable();
    });
  }

  function exportToExcel() {
    setExportLoading(true);

    const exportParams = {
      startDate: params.startDate,
      endDate: params.endDate,
      store: params.store,
    };

    reportsPerformanceService
      .exportToExcel(exportParams)
      .finally(() => setExportLoading(false));
  }

  const renderPerformanceResume = performanceResume && (
    <SelectedPeriodResume performanceResume={performanceResume} />
  );
  const renderPerformanceTable = performanceData && performanceData.results && (
    <PerformanceTable performanceData={performanceData.results} />
  );

  return (
    <Main>
      <Header />

      <S.Main>
        <Text>Analise em detalhes sua performance de atendimento.</Text>
        <S.DatePickerWrapper>
          <S.TextWrapper>
            <Heading weight={FontWeight.bold}>
              Histórico de pedidos por dia
            </Heading>
            <Text>Acompanhe a evolução do seu atendimento.</Text>
          </S.TextWrapper>
          <DateRangePicker
            onChangeRange={changeDateRange}
            onSubmit={submitForm}
            onReset={resetForm}
            values={values}
          />
        </S.DatePickerWrapper>

        {isResumeLoading || isTableLoading ? (
          <S.LoadingWrapper>
            <Loading />
          </S.LoadingWrapper>
        ) : (
          <>
            {renderPerformanceResume}

            <S.ExportButtonWrapper>
              <Button disabled={exportLoading} onClick={exportToExcel}>
                {exportLoading ? (
                  <S.Icon>
                    <Loading small />
                  </S.Icon>
                ) : (
                  <S.Icon>
                    <MdOutlineFileDownload />
                  </S.Icon>
                )}
                Exportar em Excel
              </Button>
            </S.ExportButtonWrapper>

            {renderPerformanceTable}

            <TableFooter
              pagination={performanceData?.pagination}
              changeCurrentPage={changePage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              isLoading={isValidating}
            />
          </>
        )}
      </S.Main>
    </Main>
  );
}
